import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { get } from "./api";
import type { Company } from "./types";

/**
 * Selector global de empresa (Todas / B-Green / qiro / iaworkers). Filtra Overview, Kanban, Agentes, Costes y
 * Actividad a la vez y recuerda la elección. El filtrado lo hace el backend (`?companyId=`).
 */
interface Ctx { companyId: string | null; setCompanyId: (id: string | null) => void; companies: Company[]; current: Company | null; qs: (extra?: Record<string, string | number | undefined>) => string }
const CompanyCtx = createContext<Ctx>({ companyId: null, setCompanyId: () => {}, companies: [], current: null, qs: () => "" });
export const useCompany = () => useContext(CompanyCtx);

const KEY = "bgf-company";
const read = () => { try { return localStorage.getItem(KEY); } catch { return null; } };

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<string | null>(read);
  const q = useQuery({ queryKey: ["companies"], queryFn: () => get<Company[]>("/companies"), staleTime: 30_000 });
  const companies = q.data ?? [];
  // Si la empresa guardada ya no existe (archivada, otra instalación), se vuelve a «Todas».
  const companyId = stored && companies.some((c) => c.id === stored) ? stored : null;
  const setCompanyId = useCallback((id: string | null) => {
    setStored(id);
    try { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY); } catch { /* sin almacenamiento */ }
  }, []);
  const value = useMemo<Ctx>(() => ({
    companyId, setCompanyId, companies, current: companies.find((c) => c.id === companyId) ?? null,
    qs: (extra = {}) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries({ ...extra, companyId: companyId ?? undefined })) if (v !== undefined) p.set(k, String(v));
      const s = p.toString();
      return s ? `?${s}` : "";
    },
  }), [companyId, companies, setCompanyId]);
  return <CompanyCtx.Provider value={value}>{children}</CompanyCtx.Provider>;
}
